import { Building2, ChevronRight, Settings } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty } from '@/components/common/Empty';
import { Button } from '@/components/ui/button';
import { adminNavigationPath, TUTTI_V2_NAVIGATION } from '@/navigation/tuttiV2';
import { brandStore } from '@/stores';

export const BrandSelectionPage = observer(() => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!brandStore.initialized && !brandStore.loading) {
      void brandStore.fetchBrands().catch(() => undefined);
    }
  }, []);

  const handleEnterBrand = (brandId: string) => {
    const selected = brandStore.setActiveBrand(brandId);
    if (!selected) {
      return;
    }
    navigate(`/brands/${brandId}/beta-management`);
  };

  const handleEnterTuttiV2 = () => {
    navigate(adminNavigationPath(TUTTI_V2_NAVIGATION.brand.entryRoute));
  };

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-foreground">
              选择 Brand
            </h1>
            <p className="text-sm text-muted-foreground">
              选择业务 Brand，或进入 TUTTI V2 的独立运营能力。
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/brands')}>
            <Settings className="mr-2 h-4 w-4" />
            Brand 管理
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  {TUTTI_V2_NAVIGATION.brand.name}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  系统运营 Brand
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {TUTTI_V2_NAVIGATION.brand.description}
                </p>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-subtle text-accent">
                <Building2 className="h-4 w-4" />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                可用
              </span>
              <Button size="sm" onClick={handleEnterTuttiV2}>
                进入
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>

          {brandStore.loading && brandStore.sortedBrands.length === 0 ? (
            <div className="rounded-lg border border-border bg-white p-10 text-center text-sm text-muted-foreground md:col-span-1 xl:col-span-2">
              正在加载业务 Brand 列表...
            </div>
          ) : null}
          {!brandStore.loading && brandStore.sortedBrands.length === 0 ? (
            <div className="rounded-lg border border-border bg-white md:col-span-1 xl:col-span-2">
              <Empty
                description={
                  brandStore.error
                    ? `加载失败：${brandStore.error}`
                    : '暂无业务 Brand，请先前往 Brand 管理创建'
                }
                className="py-10"
              />
            </div>
          ) : null}
          {brandStore.sortedBrands.map((brand) => (
            <div
              key={brand.id}
              className="rounded-lg border border-border bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-foreground">
                    {brand.name}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Brand ID: {brand.id}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <span>App ID: {brand.appId}</span>
                    {!brand.customAppId ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                        默认使用 Brand ID
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-subtle text-accent">
                  <Building2 className="h-4 w-4" />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                  可用
                </span>
                <Button size="sm" onClick={() => handleEnterBrand(brand.id)}>
                  进入
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
